import React from 'react';
import { motion } from 'framer-motion';
import { Palette, Layers, Type, ChevronRight, RotateCw, Maximize2, Save, BoxIcon } from 'lucide-react';
type Variant = 'baseball' | 'basketball' | 'football';
interface BuilderVisualProps {
  theme?: 'light' | 'dark';
  variant?: Variant;
}
const VARIANT_CONFIG: Record<Variant, {
  sport: string;
  product: string;
  jerseyNumber: string;
  primaryColor: string;
  secondaryColor: string;
}> = {
  baseball: {
    sport: 'Baseball',
    product: 'Full Button Jersey',
    jerseyNumber: '07',
    primaryColor: '#D71920',
    secondaryColor: '#0B0B0F'
  },
  basketball: {
    sport: 'Basketball',
    product: 'Reversible Jersey',
    jerseyNumber: '23',
    primaryColor: '#0B0B0F',
    secondaryColor: '#D71920'
  },
  football: {
    sport: 'Football',
    product: 'Tackle Jersey',
    jerseyNumber: '12',
    primaryColor: '#1E3A8A',
    secondaryColor: '#FBBF24'
  }
};
export const BuilderVisual = ({
  theme = 'dark',
  variant = 'baseball'
}: BuilderVisualProps) => {
  const isDark = theme === 'dark';
  const config = VARIANT_CONFIG[variant];
  return <div className={`relative rounded-2xl overflow-hidden shadow-2xl border w-full ${isDark ? 'border-white/10 bg-[#0F0F12]' : 'border-gray200 bg-white'}`} data-id="element-46">
      <div className={`px-5 py-3.5 flex items-center justify-between border-b ${isDark ? 'bg-[#16161B] border-white/10' : 'bg-gray50 border-gray200'}`} data-id="element-47">
        <div className="flex items-center gap-3 min-w-0" data-id="element-48">
          <div className="w-8 h-8 rounded-md bg-brandRed flex items-center justify-center shrink-0" data-id="element-49">
            <div className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-50" />
          </div>
          <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-ink'}`} data-id="element-51">
            Uniform Designer
          </div>
          <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} hidden sm:block truncate`} data-id="element-52">
            / {config.sport} / {config.product}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0" data-id="element-53">
          <button className={`p-1.5 rounded ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray100 text-gray-500'}`} data-id="element-54">
            <RotateCw className="w-4 h-4" data-id="element-55" />
          </button>
          <button className={`p-1.5 rounded ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray100 text-gray-500'}`} data-id="element-56">
            <Maximize2 className="w-4 h-4" data-id="element-57" />
          </button>
          <button className="bg-brandRed text-white text-xs font-bold py-1.5 px-3 rounded flex items-center gap-1.5 ml-1" data-id="element-58">
            <Save className="w-3 h-3" data-id="element-59" /> Save Design
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 h-[360px] md:h-[440px]" data-id="element-60">
        <div className={`col-span-2 border-r ${isDark ? 'border-white/10 bg-[#0B0B0F]' : 'border-gray200 bg-gray50'} py-4 flex flex-col items-center gap-2`} data-id="element-61">
          {[{
          icon: Palette,
          active: true,
          label: 'Color'
        }, {
          icon: Layers,
          active: false,
          label: 'Pattern'
        }, {
          icon: Type,
          active: false,
          label: 'Text'
        }, {
          icon: BoxIcon,
          active: false,
          label: 'Fabric'
        }].map((tool, i) => <button key={i} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${tool.active ? 'bg-brandRed text-white' : isDark ? 'text-gray-400 hover:bg-white/10' : 'text-gray-500 hover:bg-gray100'}`} title={tool.label} data-id="element-62">
              <tool.icon className="w-5 h-5" data-id="element-63" />
            </button>)}
        </div>

        <div className={`col-span-7 relative ${isDark ? 'bg-gradient-to-b from-[#1A1A20] to-[#0F0F12]' : 'bg-gradient-to-b from-white to-gray50'} flex items-center justify-center p-6 overflow-hidden`} data-id="element-64">
          <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: isDark ? 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)' : 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} data-id="element-65"></div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="relative z-10" data-id="element-66">
            <JerseyMockup variant={variant} config={config} data-id="element-67" />
          </motion.div>

          <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isDark ? 'bg-white/10 text-white backdrop-blur-sm' : 'bg-ink text-white'}`} data-id="element-68">
            3D Preview
          </div>
          <div className={`absolute bottom-4 right-4 px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-2 ${isDark ? 'bg-white/10 text-gray-300 backdrop-blur-sm' : 'bg-white text-charcoal border border-gray200'}`} data-id="element-69">
            <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-70"></div>
            Live render
          </div>
        </div>

        <div className={`col-span-3 border-l ${isDark ? 'border-white/10 bg-[#0B0B0F]' : 'border-gray200 bg-gray50'} p-3 md:p-4 flex flex-col gap-4`} data-id="element-71">
          <div data-id="element-72">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-73">
              Primary Color
            </div>
            <div className="grid grid-cols-4 gap-1.5" data-id="element-74">
              {['#D71920', '#0B0B0F', '#FFFFFF', '#1E3A8A', '#FBBF24', '#16A34A', '#7C3AED', '#EC4899'].map((color, i) => {
              const isActive = color === config.primaryColor;
              return <button key={i} className={`aspect-square rounded ${isActive ? 'ring-2 ring-offset-1 ring-brandRed ' + (isDark ? 'ring-offset-[#0B0B0F]' : 'ring-offset-gray50') : ''}`} style={{
                backgroundColor: color,
                border: color === '#FFFFFF' ? '1px solid #E5E7EB' : 'none'
              }} data-id="element-75" />;
            })}
            </div>
          </div>

          <div data-id="element-76">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-77">
              Pattern
            </div>
            <div className="grid grid-cols-3 gap-1.5" data-id="element-78">
              {[0, 1, 2].map(i => <div key={i} className={`aspect-square rounded border overflow-hidden ${i === 0 ? 'border-brandRed' : isDark ? 'border-white/10' : 'border-gray200'} ${isDark ? 'bg-white/5' : 'bg-white'}`} data-id="element-79">
                  {i === 0 && <div className="w-full h-full" style={{
                backgroundColor: config.primaryColor
              }} data-id="element-80"></div>}
                  {i === 1 && <div className="w-full h-full flex" data-id="element-81">
                      <div className="flex-1" style={{
                  backgroundColor: config.primaryColor
                }} data-id="element-82"></div>
                      <div className="flex-1 bg-white" data-id="element-83"></div>
                      <div className="flex-1" style={{
                  backgroundColor: config.primaryColor
                }} data-id="element-84"></div>
                    </div>}
                  {i === 2 && <div className="w-full h-full" style={{
                background: `linear-gradient(to bottom, ${config.primaryColor}, ${config.secondaryColor})`
              }} data-id="element-85"></div>}
                </div>)}
            </div>
          </div>

          <div className="hidden lg:block" data-id="element-86">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-87">
              Fabric
            </div>
            <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-charcoal'} font-semibold border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray200 bg-white'} rounded px-2.5 py-2 flex items-center justify-between`} data-id="element-88">
              <span data-id="element-89">
                {variant === 'basketball' ? 'CoolDry' : variant === 'football' ? 'Protech' : 'Polydex'}
              </span>
              <ChevronRight className="w-3 h-3 opacity-50" data-id="element-90" />
            </div>
          </div>

          <div className="hidden lg:block" data-id="element-91">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-92">
              Number
            </div>
            <div className={`text-2xl font-heading font-extrabold ${isDark ? 'text-white' : 'text-ink'}`} data-id="element-93">
              {config.jerseyNumber}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
interface JerseyMockupProps {
  variant: Variant;
  config: (typeof VARIANT_CONFIG)[Variant];
}
const JerseyMockup = ({
  variant,
  config
}: JerseyMockupProps) => {
  if (variant === 'basketball') {
    // Basketball tank top mockup
    return <svg viewBox="0 0 200 240" className="w-44 h-52 md:w-56 md:h-64 drop-shadow-2xl" data-id="element-94">
        <defs data-id="element-95">
          <linearGradient id="bballBody" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-96">
            <stop offset="0%" stopColor={config.primaryColor} data-id="element-97" />
            <stop offset="100%" stopColor={config.primaryColor} stopOpacity="0.85" data-id="element-98" />
          </linearGradient>
        </defs>
        {/* Tank body */}
        <path d="M55 50 L75 35 L75 55 L100 60 L125 55 L125 35 L145 50 L155 90 L155 215 Q155 225 145 225 L55 225 Q45 225 45 215 L45 90 Z" fill="url(#bballBody)" stroke={config.secondaryColor} strokeWidth="2" data-id="element-99" />
        {/* Armholes */}
        <ellipse cx="50" cy="80" rx="8" ry="22" fill="#FFFFFF" opacity="0.1" data-id="element-100" />
        <ellipse cx="150" cy="80" rx="8" ry="22" fill="#FFFFFF" opacity="0.1" data-id="element-101" />
        {/* Number */}
        <text x="100" y="155" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="68" fill={config.secondaryColor} data-id="element-102">
          {config.jerseyNumber}
        </text>
        {/* Bottom band */}
        <rect x="45" y="215" width="110" height="10" fill={config.secondaryColor} opacity="0.9" data-id="element-103" />
      </svg>;
  }
  if (variant === 'football') {
    // Football jersey mockup with shoulder pads silhouette
    return <svg viewBox="0 0 200 240" className="w-44 h-52 md:w-56 md:h-64 drop-shadow-2xl" data-id="element-104">
        <defs data-id="element-105">
          <linearGradient id="footballBody" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-106">
            <stop offset="0%" stopColor={config.primaryColor} data-id="element-107" />
            <stop offset="100%" stopColor={config.primaryColor} stopOpacity="0.9" data-id="element-108" />
          </linearGradient>
        </defs>
        {/* Body with broad shoulders */}
        <path d="M40 55 L75 40 L100 45 L125 40 L160 55 L170 100 L155 105 L155 215 Q155 225 145 225 L55 225 Q45 225 45 215 L45 105 L30 100 Z" fill="url(#footballBody)" stroke="#0B0B0F" strokeWidth="1.5" data-id="element-109" />
        {/* Collar */}
        <path d="M75 40 L100 45 L125 40 L125 60 L100 65 L75 60 Z" fill="#0B0B0F" data-id="element-110" />
        {/* Sleeve stripes */}
        <rect x="30" y="100" width="15" height="8" fill={config.secondaryColor} data-id="element-111" />
        <rect x="155" y="100" width="15" height="8" fill={config.secondaryColor} data-id="element-112" />
        {/* Number */}
        <text x="100" y="150" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="62" fill={config.secondaryColor} data-id="element-113">
          {config.jerseyNumber}
        </text>
      </svg>;
  }
  // Default: baseball
  return <svg viewBox="0 0 200 240" className="w-44 h-52 md:w-56 md:h-64 drop-shadow-2xl" data-id="element-114">
      <defs data-id="element-115">
        <linearGradient id="baseballBody" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-116">
          <stop offset="0%" stopColor="#E91E27" data-id="element-117" />
          <stop offset="100%" stopColor="#A8141A" data-id="element-118" />
        </linearGradient>
      </defs>
      <path d="M50 40 L80 25 L100 35 L120 25 L150 40 L165 80 L150 90 L150 200 Q150 215 135 215 L65 215 Q50 215 50 200 L50 90 L35 80 Z" fill="url(#baseballBody)" stroke="#0B0B0F" strokeWidth="1.5" data-id="element-119" />
      <path d="M80 25 L100 35 L120 25 L120 50 L100 55 L80 50 Z" fill="#0B0B0F" data-id="element-120" />
      {/* Button placket */}
      <line x1="100" y1="55" x2="100" y2="210" stroke="#0B0B0F" strokeWidth="1" opacity="0.5" data-id="element-121" />
      <circle cx="100" cy="80" r="2" fill="#0B0B0F" data-id="element-122" />
      <circle cx="100" cy="110" r="2" fill="#0B0B0F" data-id="element-123" />
      <circle cx="100" cy="140" r="2" fill="#0B0B0F" data-id="element-124" />
      <circle cx="100" cy="170" r="2" fill="#0B0B0F" data-id="element-125" />
      <text x="100" y="145" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="900" fontSize="52" fill="#FFFFFF" data-id="element-126">
        {config.jerseyNumber}
      </text>
    </svg>;
};